import React, { Component } from 'react';
import PropTypes from 'prop-types';
import injectSheet from 'react-jss';
import { connect } from 'react-redux';
import { compose } from 'redux';
import memoize from 'memoize-one';
import cn from 'classnames';
import get from '../helpers/get';
import {
  responsiveImageAlternativeType,
  responsiveImageType,
  responsiveVideoType,
} from '../propTypes';
import ResponsiveImage from './ResponsiveImage';
import Video from './Video';
import { getBestAlternative } from '../helpers/getResponsiveAlternative';
import { isCurrentBreakpointAtLeast, isSoundMuted, getSliceContentType } from '../selectors';
import getImageFromVideo from '../helpers/getImageFromVideo';
import createElementResizeDetectorRef from '../helpers/createElementResizeDetectorRef';
import ToggleAudioButton from './ToggleAudioButton';

class ImageOrVideo extends Component {
  state = {
    width: undefined,
    height: undefined,
  };

  shouldComponentUpdate(nextProps, nextState) {
    const {
      currentImageAlternative: thisCurrentImage,
      videoPaused: currVideoPaused,
      playButtonClicked: currPlayButtonClicked,
    } = this.props;
    const {
      currentImageAlternative: nextCurrentImage,
      videoPaused: nextVideoPaused,
      playButtonClicked: nextPlayButtonClicked,
    } = nextProps;

    const { width: currWidth, height: currHeight } = this.state;
    const { width: nextWidth, height: nextHeight } = nextState;

    const blurImgFromThisImage = get(thisCurrentImage, ['blur']);
    const blurImgFromNextImage = get(nextCurrentImage, ['blur']);
    const hasNewImage = blurImgFromThisImage !== blurImgFromNextImage;

    const isNotSameWidth = currWidth !== nextWidth;
    const isNotSameHeight = currHeight !== nextHeight;
    const hasChangeDimensions = isNotSameWidth || isNotSameHeight;

    const hasChangeVideoPaused = currVideoPaused !== nextVideoPaused;
    const hasChangePlayButtonClicked = currPlayButtonClicked !== nextPlayButtonClicked;

    return hasNewImage || hasChangeDimensions || hasChangeVideoPaused || hasChangePlayButtonClicked;
  }

  componentWillUnmount() {
    this.unmount = true;
  }

  setRef = createElementResizeDetectorRef(({ width, height }) => {
    if (this.unmount) return;
    this.setState({ width, height });
  });

  renderVideo() {
    const { height, width } = this.state;
    if (width !== undefined) {
      const {
        classes, loopVideo, onVideoEnded, onVideoPlaying, onVideoProgress, onVideoPaused,
        video, videoCurrentTime, videoPaused, isMuted, videoClassName,
        contentType, autoPlay, restartOnPause,
        setVideoRef, showAudioButton, playButtonClicked, onSpacePressed, onVideoPlay,
      } = this.props;

      return (
        <>
          <Video
            className={classes.video}
            currentTime={videoCurrentTime}
            loop={loopVideo}
            onEnded={onVideoEnded}
            onPlaying={onVideoPlaying}
            onProgress={onVideoProgress}
            onPaused={onVideoPaused}
            paused={videoPaused}
            video={video}
            viewportHeight={height}
            viewportWidth={width}
            autoPlay={autoPlay}
            restartOnPause={restartOnPause}
            setVideoRef={setVideoRef}
            playButtonClicked={playButtonClicked}
            onSpacePressed={onSpacePressed}
            onVideoPlay={onVideoPlay}
          />
          {showAudioButton && (
            <ToggleAudioButton
              className={videoClassName}
              muted={isMuted}
              contentType={contentType}
            />
          )}
        </>
      );
    }
    return null;
  }

  render() {
    const {
      aspect, classes, className, currentImageAlternative: currentImage, defaultAlt, imageClassName,
      validImage: image,
    } = this.props;
    const hasVideo = !!get(this.props, ['video', 'sizes', 'length']);
    return (
      <div className={cn(className, classes.wrapper)} ref={this.setRef}>
        <ResponsiveImage
          aspect={aspect}
          className={cn(classes.image, imageClassName)}
          images={get(currentImage, ['sizes'])}
          alt={get(image, ['alt']) || defaultAlt}
          blur={get(currentImage, ['blur'])}
        />
        {hasVideo && this.renderVideo()}
      </div>
    );
  }
}

ImageOrVideo.propTypes = {
  aspect: PropTypes.number,
  className: PropTypes.string,
  classes: PropTypes.objectOf(PropTypes.string).isRequired,
  currentImageAlternative: responsiveImageAlternativeType,
  defaultAlt: PropTypes.string,
  // eslint-disable-next-line react/no-unused-prop-types -- used in mapStateToProps
  image: responsiveImageType,
  imageClassName: PropTypes.string,
  videoClassName: PropTypes.string,
  loopVideo: PropTypes.bool,
  onVideoEnded: PropTypes.func,
  onVideoPlaying: PropTypes.func,
  onVideoProgress: PropTypes.func,
  onVideoPaused: PropTypes.func,
  validImage: responsiveImageType,
  video: responsiveVideoType,
  videoCurrentTime: PropTypes.number,
  videoPaused: PropTypes.bool,
  isMuted: PropTypes.bool,
  autoPlay: PropTypes.bool,
  contentType: PropTypes.string,
  restartOnPause: PropTypes.bool,
  showAudioButton: PropTypes.bool,
  setVideoRef: PropTypes.func,
  playButtonClicked: PropTypes.bool,
  onSpacePressed: PropTypes.func,
  onVideoPlay: PropTypes.func,
};

ImageOrVideo.defaultProps = {
  aspect: undefined,
  className: undefined,
  currentImageAlternative: undefined,
  defaultAlt: undefined,
  image: null,
  imageClassName: undefined,
  videoClassName: undefined,
  loopVideo: true,
  onVideoEnded: undefined,
  onVideoPlaying: undefined,
  onVideoProgress: undefined,
  onVideoPaused: undefined,
  validImage: undefined,
  video: null,
  videoCurrentTime: 0,
  videoPaused: false,
  isMuted: false,
  autoPlay: true,
  contentType: undefined,
  restartOnPause: true,
  showAudioButton: true,
  setVideoRef: undefined,
  playButtonClicked: true,
  onSpacePressed: undefined,
  onVideoPlay: undefined,
};

const mapStateToProps = () => {
  const getValidImage = memoize((image, video) => image || getImageFromVideo(video));
  const getCurrentImageAlternative = memoize(getBestAlternative);
  return (state, { image, video, carouselId }) => {
    const isSmall = !isCurrentBreakpointAtLeast(state, 'md');
    const validImage = getValidImage(image, video);
    return {
      validImage,
      currentImageAlternative: getCurrentImageAlternative(validImage, isSmall),
      isSmall,
      isMuted: isSoundMuted(state),
      contentType: carouselId && getSliceContentType(state, carouselId),
    };
  };
};

export default compose(
  connect(mapStateToProps),

  injectSheet({
    wrapper: {
      position: 'relative',
      overflow: 'hidden',
    },
    video: {
      position: 'absolute',
      left: 0,
      top: 0,
      width: '100%',
      height: '100%',
      opacity: 0,
      transition: 'opacity 0.4s ease-out',
      // to fix not visible outline on Safari and Firefox (overflow: hidden for parent)
      // focus-visible isn't supported on Safari
      '&:focus': {
        outlineOffset: -5,
      },
      '&:focus-visible': {
        outlineOffset: -1,
      },
    },
  }),
)(ImageOrVideo);
